// src/components/layout/Sidebar.jsx
import React, { useMemo, useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { initials } from '../../utils/helpers';
import { buAPI } from '../../services/api';
import {
  LayoutDashboard, FileText, Bell, Settings, LogOut,
  ChevronLeft, ChevronRight, Kanban, Sparkles, ShieldCheck,
  Users, Crown, HeartHandshake, Briefcase, Building2, UserCheck, Calendar,
  NotebookPen, User, ClipboardCheck, Sun, Moon, Target, MapPin, Activity, Phone,
  Timer, ReceiptText, CalendarX, CalendarOff, ChevronDown, UsersRound, FolderKanban, Upload,
  FolderOpen, Folders, Receipt, ShoppingCart, Calculator,
} from 'lucide-react';

const BoondIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="32" height="32" rx="7" fill="#1B2A4A"/>
    <text x="16" y="22" textAnchor="middle" fontSize="18" fontWeight="800" fontFamily="Arial,sans-serif" fill="#FFFFFF">B</text>
  </svg>
);
import './Sidebar.css';


// ── Entrées nav ───────────────────────────────────────────────
const NAV_DASHBOARD     = { to: '/dashboard',         icon: LayoutDashboard, label: 'Dashboard' };
const NAV_KANBAN        = { to: '/kanban',             icon: Kanban,          label: 'Pipeline AO' };
const NAV_TA_BOARD      = { to: '/kanban',             icon: Kanban,          label: 'Mes missions' };
const NAV_RT_BOARD      = { to: '/rt-dashboard',       icon: ClipboardCheck,  label: 'Mes évaluations' };
const NAV_PIPELINE      = { to: '/pipeline',           icon: UserCheck,       label: 'Pipe candidats' };
const NAV_RESOURCES     = { to: '/resources',          icon: UsersRound,      label: 'Ressources' };
const NAV_COMPANIES         = { to: '/companies',          icon: Building2,  label: 'CRM Sociétés' };
const NAV_CRM_CONTACTS      = { to: '/crm-contacts',       icon: Phone,      label: 'CRM Contacts' };
const NAV_ACTIONS           = { to: '/actions',            icon: Activity,   label: 'Actions' };
const NAV_BESOINS           = { to: '/besoins',            icon: Target,     label: 'Archives des besoins' };
const NAV_POSITIONNEMENTS   = { to: '/positionnements',    icon: MapPin,     label: 'Positionnements' };
const NAV_CV            = { to: '/cv',                 icon: FileText,        label: 'WiCV', labelEl: <span>Wi<span style={{ color:'#FF4100' }}>CV</span></span> };
const NAV_CALENDAR      = { to: '/calendar',           icon: Calendar,        label: 'Calendrier' };
const NAV_NOTIFICATIONS = { to: '/notifications',      icon: Bell,            label: 'Notifications', badge: true };
const NAV_ADMIN         = { to: '/admin',              icon: Settings,        label: 'Administration' };
const NAV_MON_COMPTE    = { to: '/mon-compte',         icon: User,            label: 'Mon compte' };
const NAV_MES_DOCS      = { to: '/mes-documents',      icon: FolderOpen,      label: 'Mes Documents' };
const NAV_COLLAB_HOME   = { to: '/collab-dashboard',   icon: LayoutDashboard, label: 'Accueil' };
const NAV_EQUIPE        = { to: '/equipe',              icon: Users,           label: 'Mon équipe' };
const NAV_RH_HOME       = { to: '/rh-dashboard',       icon: HeartHandshake,  label: 'Accueil RH' };
const NAV_RH_DOCS       = { to: '/rh-documents',       icon: Folders,         label: 'Dossiers salariés' };
const NAV_COMPTABLE_HOME = { to: '/comptable-dashboard', icon: Calculator,     label: 'Accueil Comptabilité' };

const SEP_OUTILS    = { type: 'label', text: 'Outils' };
const NAV_NOTES_BTN = { type: 'sticky-notes' }; // bouton Mes Notes dans la section Outils

const SEP_RH        = { type: 'label', text: 'Activité & Frais' };
const NAV_HR_MENU   = { type: 'hr-menu' };
const NAV_BOOND_MENU = { type: 'boond-menu' };
const NAV_CRM_MENU  = { type: 'crm-menu' };

const CRM_SUBMENU = [
  { to: '/companies',    icon: Building2, label: 'Sociétés' },
  { to: '/crm-contacts', icon: Phone,     label: 'Contacts' },
];

const BOOND_SUBMENU = [
  { to: '/admin/import', icon: Upload, label: 'Import CSV Boond' },
];

const NAV_PROJECTS  = { to: '/projects',       icon: FolderKanban, label: 'Projets' };
const NAV_INVOICES_SALE = { to: '/sale-invoices',     icon: Receipt,      label: 'Factures de ventes' };
const NAV_INVOICES_BUY  = { to: '/purchase-invoices', icon: ShoppingCart, label: "Factures d'achats" };

const HR_SUBMENU = [
  { to: '/timesheets/history', icon: Timer,          label: 'Feuilles de temps' },
  { to: '/notes-frais',       icon: ReceiptText,    label: 'Note et frais' },
  { to: '/absences',          icon: CalendarX,      label: 'Demande d\'absences' },
  { to: '/arrets-maladie',    icon: Activity,       label: 'Arrêts maladie' },
  { to: '/absences-imposees', icon: CalendarOff,    label: 'Absences imposées' },
  { to: '/rh-validations',    icon: ClipboardCheck, label: 'Validations', adminOnly: true },
];


// ── Navigation pilotée par les permissions ────────────────────
function buildNavFromPermissions(perms, isSuperAdmin, role) {
  const has = (key) => isSuperAdmin || (perms || []).includes(key);
  const items = [];

  if (role === 'collaborateur') {
    items.push(NAV_COLLAB_HOME);
    items.push(NAV_PROJECTS);
    items.push(NAV_EQUIPE);
  } else if (role === 'rh') {
    items.push(NAV_RH_HOME);
  } else if (role === 'comptable') {
    items.push(NAV_COMPTABLE_HOME);
  } else if (has('admin_kpi')) {
    items.push(NAV_DASHBOARD);
  }

  // AO : pipeline complet OU board TA OU board RT (exclusif)
  if (has('ao_pipeline')) {
    items.push(NAV_KANBAN);
  } else if (has('ao_kanban')) {
    items.push(NAV_TA_BOARD);
  }
  if (has('rt_reports') && !has('ao_pipeline')) items.push(NAV_RT_BOARD);

  if (has('candidates'))        items.push(NAV_PIPELINE);
  if (has('resources'))         items.push(NAV_RESOURCES);
  if (has('projects'))          items.push(NAV_PROJECTS);
  if (has('projects'))          items.push(NAV_INVOICES_SALE, NAV_INVOICES_BUY);
  if (has('crm'))               items.push(NAV_CRM_MENU);
  if (has('besoins'))           items.push(NAV_BESOINS);
  if (has('positionnements'))   items.push(NAV_POSITIONNEMENTS);
  if (has('actions'))           items.push(NAV_ACTIONS);

  items.push(NAV_NOTIFICATIONS);

  // Section Outils
  const hasTools = has('candidates_cv') || has('calendar');
  items.push(SEP_OUTILS);
  if (has('candidates_cv')) items.push(NAV_CV);
  if (has('calendar'))      items.push(NAV_CALENDAR);
  items.push(NAV_NOTES_BTN);

  if (has('timesheets')) {
    items.push(SEP_RH);
    items.push(NAV_HR_MENU);
  }

  items.push(NAV_MES_DOCS);

  // RH : accès direct aux documents salariés + annuaire
  if (role === 'rh') {
    items.push(NAV_RH_DOCS);
    items.push(NAV_EQUIPE);
  }

  if (isSuperAdmin) items.push(NAV_BOOND_MENU);

  return items;
}


// ── Badge rôle ────────────────────────────────────────────────
const ROLE_BADGE = {
  super_admin:        { label: 'Super Admin',         icon: Crown,           color: '#7C3AED' },
  admin:              { label: 'Business Manager',      icon: ShieldCheck,     color: '#DC2626' },
  rh:                 { label: 'Ressources Humaines',  icon: HeartHandshake,  color: '#059669' },
  business_manager:   { label: "Ingénieur d'Affaires",  icon: LayoutDashboard, color: '#3B82F6' },
  referent_technique: { label: 'Référent Technique',    icon: ClipboardCheck,  color: '#0891B2' },
  talent_acquisition: { label: 'Talent Acquisition',    icon: Users,           color: '#EA580C' },
  ingenieur_affaires: { label: "Ingénieur d'Affaires",  icon: LayoutDashboard, color: '#3B82F6' },
  collaborateur:      { label: 'Collaborateur',          icon: Briefcase,       color: '#0891B2' },
  comptable:          { label: 'Comptable',              icon: Calculator,      color: '#7C3AED' },
};


function BrandMark({ collapsed = false }) {
  return (
    <div className={`sidebar-brandmark ${collapsed ? 'is-collapsed' : ''}`} aria-hidden="true">
      <span className="sidebar-brandmark-bar" />
      <span className="sidebar-brandmark-bar" />
      <span className="sidebar-brandmark-bar" />
    </div>
  );
}


export default function Sidebar({ unreadCount = 0, onToggleStickyNotes, stickyNotesOpen = false, stickyNotesCount = 0, mobileOpen = false, onCloseMobile }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [hrOpen,    setHrOpen]    = useState(false);
  const [crmOpen,   setCrmOpen]   = useState(false);
  const [boondOpen, setBoondOpen] = useState(false);
  const [myBUs, setMyBUs] = useState([]);
  const [isLight, setIsLight] = useState(() => {
    const stored = localStorage.getItem('wib-theme') || localStorage.getItem('wib-sidebar') || 'dark';
    return stored === 'light';
  });

  useEffect(() => {
    const theme = isLight ? 'light' : 'dark';
    localStorage.setItem('wib-theme', theme);
    localStorage.setItem('wib-sidebar', theme);
    document.documentElement.setAttribute('data-theme', theme);
  }, [isLight]);

  // Initialise data-theme au montage
  useEffect(() => {
    const stored = localStorage.getItem('wib-theme') || localStorage.getItem('wib-sidebar') || 'dark';
    document.documentElement.setAttribute('data-theme', stored);
  }, []);

  useEffect(() => {
    if (user) {
      buAPI.getMyBUs().then(r => setMyBUs(r.data || [])).catch(() => {});
    }
  }, [user?.id]);

  const roleRaw        = (user?.role || '').toLowerCase();
  const isSuperAdmin   = roleRaw === 'super_admin' || user?.is_superuser;
  const perms          = user?.effective_permissions || [];
  const nav            = useMemo(
    () => buildNavFromPermissions(perms, isSuperAdmin, roleRaw),
    [JSON.stringify(perms), isSuperAdmin, roleRaw]
  );
  const roleBadge     = ROLE_BADGE[roleRaw] || ROLE_BADGE.talent_acquisition;
  const RoleBadgeIcon = roleBadge.icon;
  const fullName      = `${user?.prenom || ''} ${user?.nom || ''}`.trim();


  const handleLogout = () => { logout(); navigate('/login'); };


  return (
    <aside className={`sidebar ${collapsed ? 'sidebar--collapsed' : ''} ${isLight ? 'sidebar--light' : ''} ${mobileOpen ? 'sidebar--mobile-open' : ''}`}>
      <div className="sidebar-top">


        {/* Logo */}
        <div className="sidebar-logo">
          <div className="sidebar-logo-left">
            <BrandMark collapsed={collapsed} />
            {!collapsed && (
              <div className="sidebar-logo-text">
                <span className="sidebar-brand">WIBusiness</span>
                <span className="sidebar-brand-sub">by Wikeys</span>
              </div>
            )}
          </div>
          <div style={{ display:'flex', gap:6, alignItems:'center' }}>
            {/* Theme toggle — pill Sun/Moon */}
            <button
              type="button"
              onClick={() => setIsLight(l => !l)}
              title={isLight ? 'Mode sombre' : 'Mode clair'}
              style={{
                width: 52, height: 28,
                borderRadius: 14,
                border: `1px solid ${isLight ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)'}`,
                background: isLight
                  ? 'rgba(59,130,246,0.10)'
                  : 'rgba(255,255,255,0.08)',
                display: 'flex',
                alignItems: 'center',
                padding: '0 4px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                position: 'relative',
                flexShrink: 0,
              }}
            >
              {/* Pill indicator */}
              <span style={{
                position: 'absolute',
                width: 20, height: 20,
                borderRadius: '50%',
                background: isLight ? '#3B82F6' : 'rgba(255,255,255,0.85)',
                left: isLight ? 28 : 4,
                transition: 'left 0.2s ease',
                boxShadow: isLight ? '0 0 8px rgba(59,130,246,0.4)' : '0 1px 4px rgba(0,0,0,0.25)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {isLight
                  ? <Sun size={11} color="#f59e0b"/>
                  : <Moon size={11} color="#e0e7ff"/>
                }
              </span>
              {/* Background icons */}
              <Sun size={10} color={isLight ? '#8E8F98' : 'rgba(255,255,255,0.3)'} style={{ marginLeft: 2 }}/>
              <Moon size={10} color={isLight ? '#8E8F98' : 'rgba(255,255,255,0.3)'} style={{ marginLeft: 'auto', marginRight: 2 }}/>
            </button>

            <button className="sidebar-collapse-btn" onClick={() => setCollapsed(!collapsed)}
              title={collapsed ? 'Déplier le menu' : 'Réduire le menu'} type="button">
              {collapsed ? <ChevronRight size={15}/> : <ChevronLeft size={15}/>}
            </button>
          </div>
        </div>


        {/* Workspace card */}
        {!collapsed && (
          <div className="sidebar-workspace-card">
            <div className="sidebar-workspace-icon"><Sparkles size={16}/></div>
            <div className="sidebar-workspace-content">
              <div className="sidebar-workspace-title">Espace opérationnel</div>
              <div className="sidebar-workspace-desc">Gestion centralisée des workflows business.</div>
            </div>
          </div>
        )}


        {/* User */}
        <div className={`sidebar-user ${collapsed ? 'sidebar-user--collapsed' : ''}`}>
          <div className="sidebar-avatar-wrap">
            <div className="avatar avatar-md sidebar-avatar">{initials(fullName || 'WU')}</div>
            {!collapsed && <span className="sidebar-user-status"/>}
          </div>
          {!collapsed && (
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">{fullName || 'Utilisateur'}</div>
              <div style={{
                display:'inline-flex', alignItems:'center', gap:4,
                padding:'2px 8px', borderRadius:10, marginTop:2,
                background:`${roleBadge.color}18`, border:`1px solid ${roleBadge.color}44`,
              }}>
                <RoleBadgeIcon size={10} color={roleBadge.color}/>
                <span style={{ fontSize:'0.6875rem', fontWeight:700, color:roleBadge.color }}>
                  {roleBadge.label}
                </span>
              </div>
              <div className="sidebar-user-meta" style={{ marginTop:4 }}>
                <ShieldCheck size={12}/>
                <span>{user?.site || 'Wikeys'}</span>
              </div>
              {myBUs.length > 0 && (
                <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginTop:6 }}>
                  {myBUs.map(bu => (
                    <span key={bu.id} style={{
                      display:'inline-flex', alignItems:'center', gap:4,
                      padding:'3px 9px', borderRadius:8, fontSize:'0.7rem',
                      fontWeight:700, background:'rgba(59,130,246,0.12)', color:'#60A5FA',
                      border:'1px solid rgba(59,130,246,0.28)', maxWidth:120,
                      overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                    }} title={bu.name}>
                      {bu.logo_url
                        ? <img src={bu.logo_url} alt="" style={{ width:12, height:12, borderRadius:2, objectFit:'cover', flexShrink:0 }}/>
                        : <Briefcase size={10} style={{ flexShrink:0 }}/>
                      }
                      {bu.name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>


      {/* Navigation */}
      <nav className="sidebar-nav">
        {!collapsed && <div className="sidebar-nav-label">Navigation</div>}
        {nav.map((item, idx) => {
          if (item.type === 'label') {
            return !collapsed
              ? <div key={`sep-${idx}`} className="sidebar-nav-label" style={{ marginTop: 8 }}>{item.text}</div>
              : <div key={`sep-${idx}`} className="sidebar-nav-sep-collapsed"/>;
          }
          if (item.type === 'sticky-notes') {
            return (
              <button key="sticky-notes"
                className={`sidebar-sticky-btn sidebar-link ${stickyNotesOpen ? 'sidebar-sticky-btn--active active' : ''}`}
                onClick={onToggleStickyNotes}
                title={collapsed ? 'Mes Notes' : undefined}
                type="button"
              >
                <div className="sidebar-link-icon" style={{ position: 'relative' }}>
                  <NotebookPen size={18} />
                  {stickyNotesCount > 0 && <span className="sidebar-badge">{stickyNotesCount > 9 ? '9+' : stickyNotesCount}</span>}
                </div>
                {!collapsed && (
                  <>
                    <span className="sidebar-link-label">Mes Notes</span>
                    {stickyNotesCount > 0 && <span className="sidebar-badge-inline">{stickyNotesCount}</span>}
                  </>
                )}
              </button>
            );
          }
          if (item.type === 'boond-menu') {
            return (
              <div key="boond-menu" className="sidebar-hr-menu">
                <button
                  type="button"
                  className={`sidebar-link sidebar-hr-toggle ${boondOpen ? 'active' : ''}`}
                  onClick={() => setBoondOpen(o => !o)}
                  title={collapsed ? 'Boond' : undefined}
                >
                  <div className="sidebar-link-icon"><BoondIcon size={18}/></div>
                  {!collapsed && (
                    <>
                      <span className="sidebar-link-label">Boond</span>
                      <ChevronDown size={14} className={`sidebar-hr-chevron ${boondOpen ? 'sidebar-hr-chevron--open' : ''}`}/>
                    </>
                  )}
                </button>
                {boondOpen && !collapsed && (
                  <div className="sidebar-hr-submenu">
                    {BOOND_SUBMENU.map(sub => {
                      const SubIcon = sub.icon;
                      return (
                        <NavLink key={sub.to} to={sub.to} onClick={onCloseMobile}
                          className={({ isActive }) => `sidebar-hr-sublink ${isActive ? 'active' : ''}`}>
                          <SubIcon size={15}/><span>{sub.label}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                )}
                {boondOpen && collapsed && (
                  <div className="sidebar-hr-submenu-collapsed">
                    {BOOND_SUBMENU.map(sub => {
                      const SubIcon = sub.icon;
                      return (
                        <NavLink key={sub.to} to={sub.to} onClick={onCloseMobile}
                          title={sub.label}
                          className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
                          <div className="sidebar-link-icon"><SubIcon size={16}/></div>
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }
          if (item.type === 'crm-menu') {
            return (
              <div key="crm-menu" className="sidebar-hr-menu">
                <button
                  type="button"
                  className={`sidebar-link sidebar-hr-toggle ${crmOpen ? 'active' : ''}`}
                  onClick={() => setCrmOpen(o => !o)}
                  title={collapsed ? 'CRM' : undefined}
                >
                  <div className="sidebar-link-icon"><HeartHandshake size={18}/></div>
                  {!collapsed && (
                    <>
                      <span className="sidebar-link-label">CRM</span>
                      <ChevronDown size={14} className={`sidebar-hr-chevron ${crmOpen ? 'sidebar-hr-chevron--open' : ''}`}/>
                    </>
                  )}
                </button>
                {crmOpen && !collapsed && (
                  <div className="sidebar-hr-submenu">
                    {CRM_SUBMENU.map(sub => {
                      const SubIcon = sub.icon;
                      return (
                        <NavLink key={sub.to} to={sub.to} onClick={onCloseMobile}
                          className={({ isActive }) => `sidebar-hr-sublink ${isActive ? 'active' : ''}`}>
                          <SubIcon size={15}/><span>{sub.label}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                )}
                {crmOpen && collapsed && (
                  <div className="sidebar-hr-submenu-collapsed">
                    {CRM_SUBMENU.map(sub => {
                      const SubIcon = sub.icon;
                      return (
                        <NavLink key={sub.to} to={sub.to} onClick={onCloseMobile}
                          title={sub.label}
                          className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
                          <div className="sidebar-link-icon"><SubIcon size={16}/></div>
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }
          if (item.type === 'hr-menu') {
            const isCollab = roleRaw === 'collaborateur';
            const visibleHR = HR_SUBMENU.filter(s => !s.adminOnly || !isCollab);
            return (
              <div key="hr-menu" className="sidebar-hr-menu">
                <button
                  type="button"
                  className={`sidebar-link sidebar-hr-toggle ${hrOpen ? 'active' : ''}`}
                  onClick={() => setHrOpen(o => !o)}
                  title={collapsed ? 'Activité et Frais' : undefined}
                >
                  <div className="sidebar-link-icon"><Briefcase size={18}/></div>
                  {!collapsed && (
                    <>
                      <span className="sidebar-link-label">Activité et Frais</span>
                      <ChevronDown size={14} className={`sidebar-hr-chevron ${hrOpen ? 'sidebar-hr-chevron--open' : ''}`}/>
                    </>
                  )}
                </button>
                {hrOpen && !collapsed && (
                  <div className="sidebar-hr-submenu">
                    {visibleHR.map(sub => {
                      const SubIcon = sub.icon;
                      return (
                        <NavLink
                          key={sub.to}
                          to={sub.to}
                          onClick={onCloseMobile}
                          className={({ isActive }) => `sidebar-hr-sublink ${isActive ? 'active' : ''}`}
                        >
                          <SubIcon size={15}/>
                          <span>{sub.label}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                )}
                {hrOpen && collapsed && (
                  <div className="sidebar-hr-submenu-collapsed">
                    {visibleHR.map(sub => {
                      const SubIcon = sub.icon;
                      return (
                        <NavLink
                          key={sub.to}
                          to={sub.to}
                          onClick={onCloseMobile}
                          title={sub.label}
                          className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
                        >
                          <div className="sidebar-link-icon"><SubIcon size={16}/></div>
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }
          const Icon = item.icon;
          return (
            <NavLink key={item.to + item.label} to={item.to}
              title={collapsed ? item.label : undefined}
              onClick={onCloseMobile}
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
              <div className="sidebar-link-icon">
                <Icon size={18}/>
                {item.badge && unreadCount > 0 && (
                  <span className="sidebar-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
                )}
              </div>
              {!collapsed && <span className="sidebar-link-label">{item.labelEl || item.label}</span>}
              {!collapsed && item.badge && unreadCount > 0 && (
                <span className="sidebar-badge-inline">{unreadCount > 9 ? '9+' : unreadCount}</span>
              )}
            </NavLink>
          );
        })}
      </nav>


      {/* Bas sidebar : Admin + Mon compte + Déconnexion */}
      <div className="sidebar-bottom">
        {!collapsed && <div className="sidebar-bottom-label">Compte</div>}

        {(isSuperAdmin || roleRaw === 'admin' || roleRaw === 'rh' || perms.includes('admin_users')) && (
          <NavLink to="/admin" title={collapsed ? 'Administration' : undefined} onClick={onCloseMobile}
            className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
            <div className="sidebar-link-icon"><Settings size={18}/></div>
            {!collapsed && <span className="sidebar-link-label">Administration</span>}
          </NavLink>
        )}


        <NavLink to="/mon-compte" title={collapsed ? 'Mon compte' : undefined} onClick={onCloseMobile}
          className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
          <div className="sidebar-link-icon"><User size={18}/></div>
          {!collapsed && <span className="sidebar-link-label">Mon compte</span>}
        </NavLink>

        <button className="sidebar-link sidebar-logout" onClick={handleLogout}
          title={collapsed ? 'Déconnexion' : undefined} type="button">
          <div className="sidebar-link-icon"><LogOut size={18}/></div>
          {!collapsed && <span className="sidebar-link-label">Déconnexion</span>}
        </button>
      </div>
    </aside>
  );
}
